/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import { UserContainer } from './user.style';
import {
  DeleteConfirmationModal,
  DropdownButton,
  Loader,
  Table,
  TabButton,
  IconButton as IconButtonWithText,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import { userList, userDeletion } from '../../../services/bioplus-services/user';
import { useCompany } from '../../../hooks/CompanyContext';
import { IntlMessages } from '../../../helper/langHelper';
import moment from 'moment';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import { convertToUserTimeZoneDateTime } from '../../../helper/dataFormat';
import { WarningModal } from '../../../Components/uielements/deleteComfirmationModal/deleteConfirmationModal';
import BusinessGroup from '../BusinessGroup';
import { AddPlusIcon, DeleteIcon } from '../../../Components/icons';
import GroupCreationModal from '../BusinessGroup/modals/GroupCreationModal';

const NON_DELETABLE_ROLES = ['ADMIN', 'PARTNER', 'VISITOR'];

const PartnerUsers = () => {
  const [deleteUser, setDeleteUser] = useState();
  const userContext = useUserContext();
  const companyContext = useCompany();
  const companyFilter = companyContext?.company?._id;
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const { company, userPermission } = userContext;
  const userEmail = company?.createdBy;

  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'user');
  const canDownloadReport = !!isUserAllowToManage?.isReport;
  // Permission flag derived from the "user" permission object
  const canManageUser = !!isUserAllowToManage?.isManage;

  const isGroupAllowToManage = userPermission?.find((permission) => permission?.label === 'group');
  // Permission flags derived from the "group" permission object
  const canCreateGroup = !!isGroupAllowToManage?.isCreate;
  const canViewGroup = !!isGroupAllowToManage?.isView;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [name, setName] = useState();
  const [selectedTab, setSelectedTab] = useState('Member List');
  const [isOpenGroupCreation, setIsOpenGroupCreation] = useState(false);

  function extractData(dataArray) {
    if (dataArray) {
      const extractedData = dataArray?.map((item) => {
        return {
          Name: `${item?.firstName ?? ''} ${item?.lastName ?? ''}`.trim(),
          email: item?.email,
          mobile: item?.phone,
          lastLogin: item?.lastLogin
            ? convertToUserTimeZoneDateTime(item?.lastLogin, userTimeZone)
            : 'N/A',
        };
      });
      return { bioplusUsers: extractedData };
    }
  }

  // Listing query - same pattern as AdminUsers, but userType: 'PARTNER'
  const usersQuery = useQuery(['partnerUsers', companyFilter, name, page, pageSize], () =>
    userList({
      page,
      pageSize,
      userType: companyFilter ? 'OWNER' : 'PARTNER',
      companyId: companyFilter,
    })
  );
  const userTableData = usersQuery?.data?.data ?? [];
  const queryClient = useQueryClient();

  // All users query for export - same pattern as AdminUsers, but userType: 'PARTNER'
  const allUsers = useQuery(['allPartnerUsers'], () =>
    userList({
      mode: 'ALL',
      userType: companyFilter ? 'OWNER' : 'PARTNER',
      companyId: companyFilter,
    })
  );

  const deleteMutation = useMutation((data) => userDeletion(data?._id ?? data?.id), {
    onSuccess: async () => {
      alert('Member deleted successfully.');
      await queryClient.invalidateQueries('partnerUsers');
    },
  });

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleSelectedTab = (selectedTab) => {
    setSelectedTab(selectedTab);
  };

  const handleDeleteUser = (data) => {
    deleteMutation.mutate(data);
    setDeleteUser(undefined);
  };

  // Table columns - same as AdminUsers (Name, Email, Mobile, Last Login).
  const userColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.firstName ?? '--'} ${
              cellData?.row?.original?.lastName ?? '--'
            }`}
          </TableCell>
        ),
        width: '25%',
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'email',
        width: '25%',
      },
      {
        Header: <IntlMessages id="table.col.mobile.no" />,
        accessor: 'phone',
        width: '20%',
      },
      {
        Header: <IntlMessages id="table.col.last.login" />,
        accessor: 'lastLogin',
        width: '20%',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.lastLogin
              ? `${convertToUserTimeZoneDateTime(cellData?.row?.original?.lastLogin, userTimeZone)}`
              : 'N/A'}
          </TableCell>
        ),
      },
    ];

    // Only show the Actions column (Delete) if the user has manage permission
    if (canManageUser) {
      cols.push({
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
      });
    }

    return cols;
  }, [userTimeZone, canManageUser]);

  const actionList = [
    (data) => {
      const isDeleteDisabled =
        NON_DELETABLE_ROLES.includes(data?.role) || userEmail === data?.email;
      return (
        <IconButton
          onClick={() => !isDeleteDisabled && setDeleteUser(data)}
          disabled={isDeleteDisabled}
          style={isDeleteDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          <DeleteIcon style={{ width: '19px', height: '19px' }} />
        </IconButton>
      );
    },
  ];

  if (usersQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  const excelFormatData = extractData(allUsers?.data ?? []);
  const fileName = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;

  const columnConfig = [
    { header: 'Name', key: 'Name', width: 30 },
    { header: 'Email', key: 'email', width: 30 },
    { header: 'Mobile No', key: 'mobile', width: 20 },
    { header: 'Last Login', key: 'lastLogin', width: 20 },
  ];

  const handleClickItem = (item) => {
    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  return (
    <StyledContainer>
      <UserContainer>
        <HeaderTitle>
          <IntlMessages id="nav.users" />
        </HeaderTitle>{' '}
        <TwoColumns style={{ gap: '15px' }}>
          <TabButton
            buttonArray={[
              { buttonText: 'Member List', textId: '1', icon: <GroupIcon /> },
              ...(canViewGroup
                ? [
                    {
                      buttonText: 'Business Groups',
                      textId: '3',
                      icon: <GroupAddIcon />,
                    },
                  ]
                : []),
            ]}
            onButtonClick={handleSelectedTab}
            selectedTab={selectedTab}
          />

          {selectedTab === 'Member List' && excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}

          {selectedTab === 'Business Groups' && canCreateGroup && (
            <IconButtonWithText
              text={<IntlMessages id="button.label.group.creation" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setIsOpenGroupCreation(true);
              }}
            />
          )}
        </TwoColumns>
      </UserContainer>
      {selectedTab === 'Member List' ? (
        <TableWrapper>
          <Table
            data={userTableData}
            columns={userColumns}
            hasFooter={true}
            actions={actionList}
            page={page}
            pageSize={pageSize}
            prevPage={handlePreviousPage}
            nextPage={handleNextPage}
            total={usersQuery?.data?.total || 0}
          />
        </TableWrapper>
      ) : selectedTab === 'Business Groups' ? (
        <BusinessGroup />
      ) : null}
      {deleteUser && deleteUser?.role !== 'PARTNER' && deleteUser?.role !== 'ADMIN' && (
        <DeleteConfirmationModal
          onClose={() => {
            setDeleteUser(undefined);
          }}
          message={
            <IntlMessages
              id="delete.comfirmation.text.user"
              values={{
                user: ` ${deleteUser?.firstName} ${deleteUser?.lastName}`,
              }}
            />
          }
          onConfirm={() => handleDeleteUser(deleteUser)}
          isLoading={deleteMutation.isLoading}
        />
      )}
      {deleteUser && (deleteUser.role === 'PARTNER' || deleteUser.role === 'ADMIN') && (
        <WarningModal
          onClose={() => setDeleteUser(undefined)}
          message={`The user '${deleteUser?.firstName} ${deleteUser?.lastName}' assigned the '${deleteUser?.label}' role, is not permitted to perform the delete action.`}
        />
      )}{' '}
      {isOpenGroupCreation && <GroupCreationModal onClose={() => setIsOpenGroupCreation(false)} />}
    </StyledContainer>
  );
};

export default PartnerUsers;
